import { FC, FormEvent, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Snackbar,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import Container from '../../components/Container';
import BrandDialog from '../../components/BrandDialog';
import FormRow from '../../components/FormRow';
import ListPagination, {
  DEFAULT_PAGE_SIZE,
} from '../../components/ListPagination';
import { FIELD_SX, ON_PRIMARY_FIELD_SX } from '../../components/fieldStyles';
import { archivosFetch } from '../../utils/fetch';
import { colors, fontWeights } from '../../theme/tokens';
import {
  ADMIN_ACTION_LABELS,
  ADMIN_FIELD_LABELS,
  AdminEntity,
  AdminFieldChange,
  AdminHistoryEntry,
} from '../../models/adminEntity';
import { fullName, useFilterOptions } from '../Home/useFilterOptions';
import { EntitiesTable, EntityCards } from './EntitiesTable';
import {
  AdminEntityKind,
  ENTITIES_QUERY_KEY,
  EntityAction,
  EntityRow,
  LABELS,
} from './types';

// Administration of services and functions (L2-ASF).
//
// Both entities are administered through the same routes and obey the same rules, so
// one screen serves both and the kind is passed in. Keeping them apart would have meant
// two files differing only by their wording.
//
// Deletion never destroys anything: users and documents still point at these entities,
// so the backend deactivates and strikes them off instead. The wording below says so
// rather than promising a removal that does not happen.
//
// Own design (no screen in the XD — DESIGN.md, gap 3), with the pattern
// of the users list: header and tiles (Container, none active: these
// routes have no tile and are reached through the header links), page
// title, an "Ajouter un service" field with its orange button, the
// common-structure table (`Nom` · `Actif` · actions) and the dialogs in the
// blue XD modal (BrandDialog). Decisions:
//   - The "Actif" switch changes the state directly; the "•••" offers
//     the same (Activer / Désactiver) alongside Renommer, Historique and Retirer,
//     so that everything is reachable with the keyboard and on mobile.
//   - Deactivated rows dimmed; their actions stay at full opacity.
//   - Client-side pagination (the API returns the full list), with the
//     common footer, so that the screen reads like the other lists.
//   - A load failure shows the error state with "Réessayer" (it used to
//     read as an empty list); `GET /services` still answers 404 with
//     the empty list, and that really is "none yet".
//   - History: the action and the date, and who did it when the accounts
//     list allows resolving the `actor_id`.
//   - Mobile (ROADMAP.md, Part 2): cards; full-screen dialogs.

export type { AdminEntityKind } from './types';

// The backend answers in English. These are the cases a user can act on; anything else
// falls back to a generic sentence rather than showing an English message.
const MESSAGES: Record<string, string> = {
  'A name is required': 'Un nom est obligatoire.',
  'A function with that name already exists': 'Ce nom est déjà utilisé.',
  'A service with that name already exists': 'Ce nom est déjà utilisé.',
  // API gap: routes/serviceRoute.js does not translate the duplicate like
  // functionRoute.js does (409 with a sentence), and returns 400 with the
  // `toString()` of the Sequelize error. It is mapped here so that the user
  // sees the same on both screens; when the backend fixes it, this line is redundant.
  'SequelizeUniqueConstraintError: Validation error': 'Ce nom est déjà utilisé.',
  'Not found.': "Cet élément n'existe plus.",
};

const GENERIC_ERROR =
  "L'opération n'a pas pu aboutir. Veuillez réessayer dans quelques instants.";

const translate = (message?: string) =>
  (message && MESSAGES[message]) || GENERIC_ERROR;

type Notice = {
  severity: 'success' | 'error';
  text: string;
};

type Props = {
  kind: AdminEntityKind;
};

const formatAt = (value: string) =>
  new Date(value).toLocaleString('fr-FR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });

const AdminEntities: FC<Props> = ({ kind }) => {
  const labels = LABELS[kind];
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const queryClient = useQueryClient();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [newName, setNewName] = useState('');
  const [newNameError, setNewNameError] = useState<string>();
  const [editing, setEditing] = useState<EntityRow>();
  const [editedName, setEditedName] = useState('');
  const [editedNameError, setEditedNameError] = useState<string>();
  const [toBeRemoved, setToBeRemoved] = useState<EntityRow>();
  const [historyOf, setHistoryOf] = useState<EntityRow>();
  const [history, setHistory] = useState<AdminHistoryEntry[]>();
  const [togglingId, setTogglingId] = useState<string>();
  const [notice, setNotice] = useState<Notice>();

  const base = `${process.env.REACT_APP_API_URL}/api/${labels.path}`;

  useEffect(() => {
    document.title = `ArchivOs - ${labels.title}`;
    setPage(0);
    setNewName('');
    setNewNameError(undefined);
  }, [labels.title]);

  // --- Listing -----------------------------------------------------------

  const {
    data: entities = [],
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery<EntityRow[]>(
    [ENTITIES_QUERY_KEY, kind],
    async () => {
      const response = await archivosFetch(`${base}/`);
      // GET /services answers 404 on an empty list where GET /functions answers 200
      // with an empty array. An empty listing is not an error on either, so both are
      // read as "nothing yet".
      if (response.status === 404) return [];
      if (!response.ok) throw new Error(String(response.status));
      const body = await response.json();
      return Array.isArray(body?.[labels.collection])
        ? (body[labels.collection] as AdminEntity[])
        : [];
    },
    { retry: false },
  );

  const pageRows = useMemo(
    () => entities.slice(page * pageSize, page * pageSize + pageSize),
    [entities, page, pageSize],
  );

  // For "par Prénom NOM" in the history. `GET /api/users/` is
  // administrator-only, and this screen is.
  const { users } = useFilterOptions(true);
  const actorName = (actorId: string) => {
    const actor = users.find((user) => user.id === actorId);
    return actor ? fullName(actor) : '';
  };

  // The history says what a modification changed, field by field.
  const changeText = (field: string, change: AdminFieldChange): string => {
    const label = ADMIN_FIELD_LABELS[field] ?? field;

    return `${label} : « ${change.from ?? ''} » → « ${change.to ?? ''} »`;
  };

  // --- Writes --------------------------------------------------------------

  const { mutateAsync: createEntity } = useMutation((data: { name: string }) =>
    archivosFetch(`${base}/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }),
  );

  const { mutateAsync: renameEntity, isLoading: isRenaming } = useMutation(
    (data: { id: string; name: string }) =>
      archivosFetch(`${base}/${data.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: data.name }),
      }),
  );

  const { mutateAsync: setActive } = useMutation(
    (data: { id: string; active: boolean }) =>
      archivosFetch(`${base}/${data.id}/active`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: data.active }),
      }),
  );

  const { mutateAsync: removeEntity, isLoading: isRemoving } = useMutation(
    (data: { id: string }) =>
      archivosFetch(`${base}/${data.id}`, { method: 'DELETE' }),
  );

  const { mutateAsync: fetchHistory } = useMutation((data: { id: string }) =>
    archivosFetch(`${base}/${data.id}/history`),
  );

  // Every write answers the same shape, so success and failure are handled once.
  // Returns the translated error, or nothing when the change went through.
  const applyChange = async (
    run: () => Promise<Response>,
    successText: string,
  ): Promise<string | undefined> => {
    try {
      const response = await run();

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));

        if (response.status === 403) {
          return "Vous n'avez pas les droits pour cette action.";
        }

        // requireAdmin answers under `error`, every other refusal under `message`.
        return translate(body.message || body.error);
      }

      setNotice({ severity: 'success', text: successText });
      // Ten other screens read these lists through react-query. Left alone, their
      // caches would keep serving the listing from before this change.
      labels.cacheKeys.forEach((key) => queryClient.invalidateQueries([key]));
      await refetch();
      return undefined;
    } catch (_) {
      // A network failure must not read like a refusal from the server.
      return GENERIC_ERROR;
    }
  };

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const name = newName.trim();

    if (name.length === 0) {
      setNewNameError('Un nom est obligatoire.');
      return;
    }

    const error = await applyChange(
      () => createEntity({ name }),
      `« ${name} » a été ajouté.`,
    );

    if (error) {
      setNewNameError(error);
    } else {
      setNewName('');
      setNewNameError(undefined);
    }
  };

  const handleRename = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editing || isRenaming) return;

    const name = editedName.trim();
    if (name.length === 0) {
      setEditedNameError('Un nom est obligatoire.');
      return;
    }

    const error = await applyChange(
      () => renameEntity({ id: editing.id, name }),
      'Le nom a été modifié.',
    );

    if (error) {
      setEditedNameError(error);
    } else {
      setEditing(undefined);
    }
  };

  const handleToggleActive = async (entity: EntityRow) => {
    setTogglingId(entity.id);
    const error = await applyChange(
      () => setActive({ id: entity.id, active: !entity.active }),
      entity.active
        ? `${entity.name} est désactivé.`
        : `${entity.name} est réactivé.`,
    );
    setTogglingId(undefined);
    if (error) setNotice({ severity: 'error', text: error });
  };

  const handleRemove = async () => {
    if (!toBeRemoved || isRemoving) return;

    const error = await applyChange(
      () => removeEntity({ id: toBeRemoved.id }),
      `${toBeRemoved.name} a été retiré de la liste.`,
    );

    if (error) setNotice({ severity: 'error', text: error });
    setToBeRemoved(undefined);
    // Last row of a page that is not the first: step back, or the
    // page would be left empty.
    if (!error && pageRows.length === 1 && page > 0) setPage(page - 1);
  };

  const openHistory = async (entity: EntityRow) => {
    setHistoryOf(entity);
    setHistory(undefined);

    try {
      const response = await fetchHistory({ id: entity.id });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setNotice({
          severity: 'error',
          text: translate(body.message || body.error),
        });
        setHistory([]);
        return;
      }
      setHistory(Array.isArray(body?.history) ? body.history : []);
    } catch (_) {
      setNotice({ severity: 'error', text: GENERIC_ERROR });
      setHistory([]);
    }
  };

  const onAction = (action: EntityAction, entity: EntityRow) => {
    switch (action) {
      case 'rename':
        setEditing(entity);
        setEditedName(entity.name);
        setEditedNameError(undefined);
        break;
      case 'toggle':
        handleToggleActive(entity);
        break;
      case 'history':
        openHistory(entity);
        break;
      case 'remove':
        setToBeRemoved(entity);
        break;
      default:
        break;
    }
  };

  // --- Render ------------------------------------------------------------

  const listProps = {
    entities: pageRows,
    isLoading,
    isError,
    togglingId,
    onToggle: handleToggleActive,
    onAction,
    onRetry: () => refetch(),
  };

  return (
    <Container>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography component="h2" variant="h2" sx={{ color: colors.primary }}>
          {labels.title}
        </Typography>

        {/* Create: field + orange button */}
        <Box
          component="form"
          onSubmit={handleCreate}
          noValidate
          sx={(t) => ({
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            [t.breakpoints.down('tablet')]: { flexDirection: 'column' },
          })}
        >
          <TextField
            name="name"
            size="small"
            value={newName}
            onChange={(e) => {
              setNewName(e.target.value);
              setNewNameError(undefined);
            }}
            placeholder={labels.addPlaceholder}
            error={Boolean(newNameError)}
            helperText={newNameError}
            inputProps={{ 'aria-label': labels.addPlaceholder }}
            sx={(t) => ({
              ...(FIELD_SX as object),
              width: 360,
              maxWidth: '100%',
              [t.breakpoints.down('tablet')]: { width: '100%' },
            })}
          />
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            sx={(t) => ({
              py: 0.9,
              whiteSpace: 'nowrap',
              [t.breakpoints.down('tablet')]: { width: '100%' },
            })}
          >
            Ajouter
          </Button>
        </Box>

        {/* Progress while reloading after a change, without emptying the table */}
        <Box sx={{ height: 4 }}>
          {isFetching && !isLoading && (
            <LinearProgress color="secondary" sx={{ height: 2 }} />
          )}
        </Box>

        {isMobile ? (
          <EntityCards {...listProps} />
        ) : (
          <EntitiesTable {...listProps} />
        )}

        <ListPagination
          count={entities.length}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
        />
      </Box>

      {/* Renommer */}
      <BrandDialog
        open={Boolean(editing)}
        title="Renommer"
        maxWidth="xs"
        onClose={() => setEditing(undefined)}
        onSubmit={handleRename}
        busy={isRenaming}
        actions={
          <>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setEditing(undefined)}
              disabled={isRenaming}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              disabled={isRenaming}
            >
              Enregistrer
            </Button>
          </>
        }
      >
        <FormRow
          label="Nom"
          htmlFor="entity-name"
          labelWidth={60}
          error={editedNameError}
        >
          <TextField
            id="entity-name"
            name="name"
            size="small"
            fullWidth
            autoFocus
            value={editedName}
            onChange={(e) => {
              setEditedName(e.target.value);
              setEditedNameError(undefined);
            }}
            error={Boolean(editedNameError)}
            disabled={isRenaming}
            sx={ON_PRIMARY_FIELD_SX}
          />
        </FormRow>
      </BrandDialog>

      {/* Retirer de la liste */}
      <BrandDialog
        open={Boolean(toBeRemoved)}
        title="Retirer de la liste"
        maxWidth="xs"
        onClose={() => setToBeRemoved(undefined)}
        busy={isRemoving}
        actions={
          <>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => setToBeRemoved(undefined)}
              disabled={isRemoving}
            >
              Annuler
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleRemove}
              disabled={isRemoving}
              autoFocus
            >
              Retirer
            </Button>
          </>
        }
      >
        <Typography>
          Voulez-vous retirer {labels.one} « {toBeRemoved?.name} » ? Il ne
          sera plus proposé, mais les utilisateurs et les documents qui s'y
          réfèrent le conservent.
        </Typography>
      </BrandDialog>

      {/* Historique */}
      <BrandDialog
        open={Boolean(historyOf)}
        title={`Historique — ${historyOf?.name ?? ''}`}
        maxWidth="sm"
        onClose={() => setHistoryOf(undefined)}
        actions={
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setHistoryOf(undefined)}
          >
            Fermer
          </Button>
        }
      >
        {history === undefined && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress color="inherit" />
          </Box>
        )}

        {history && history.length === 0 && (
          <Typography sx={{ opacity: 0.85 }}>
            Aucune modification enregistrée.
          </Typography>
        )}

        {history && history.length > 0 && (
          <Box
            component="ul"
            aria-label="Modifications"
            sx={{ listStyle: 'none', m: 0, p: 0 }}
          >
            {history.map((entry, index) => {
              const actor = actorName(entry.actor_id);
              return (
                <Box
                  component="li"
                  key={`${entry.at}-${index}`}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: 2,
                    py: 1,
                    borderBottom: '1px solid rgba(255, 255, 255, 0.24)',
                    '&:last-of-type': { borderBottom: 'none' },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: fontWeights.medium }}>
                      {ADMIN_ACTION_LABELS[entry.action] || entry.action}
                    </Typography>
                    {entry.changes &&
                      Object.entries(entry.changes).map(([field, change]) => (
                        <Typography
                          key={field}
                          sx={{ fontSize: '0.875rem', wordBreak: 'break-word' }}
                        >
                          {changeText(field, change)}
                        </Typography>
                      ))}
                    {actor && (
                      <Typography sx={{ fontSize: '0.875rem', opacity: 0.85 }}>
                        par {actor}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    sx={{ fontSize: '0.875rem', opacity: 0.85, flexShrink: 0 }}
                  >
                    {formatAt(entry.at)}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </BrandDialog>

      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={Boolean(notice)}
        autoHideDuration={notice?.severity === 'error' ? 6000 : 4000}
        disableWindowBlurListener
        onClose={() => setNotice(undefined)}
      >
        <Alert
          severity={notice?.severity ?? 'success'}
          variant="filled"
          onClose={() => setNotice(undefined)}
          sx={{ width: '100%' }}
        >
          {notice?.text}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminEntities;
